import { FmdGoodOutlined, Fullscreen, RestartAlt } from '@mui/icons-material';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import pages from 'src/router/routes';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';

import { isAllSelection } from '../../../../utils/selection';
import DevicesMap from '../../../map/components/DevicesMap';
import MapFullscreenModal from './MapFullscreenModal';

interface MapWidgetProps {
  title?: string;
  subheader?: string;
  avatar?: JSX.Element;
  mapContent?: ReactNode;
  renderFullscreen?: (props: {
    open: boolean;
    onClose: () => void;
  }) => ReactNode;
  onHeaderClick?: () => void;
  requireApplicationSelection?: boolean;
  resetBoundsEventName?: string;
}

const MapWidget = ({
  title = 'Device Map',
  subheader = 'Configure device locations in their settings',
  avatar,
  mapContent,
  renderFullscreen,
  onHeaderClick,
  requireApplicationSelection = true,
  resetBoundsEventName = 'reset-map-bounds',
}: MapWidgetProps) => {
  /* Hooks & Use Effects */

  const theme = useTheme();

  const navigate = useNavigate();
  const { getApplicationScopedPath } = useAdminUrlNavigation();

  const appId = useEffectiveApplicationId();

  /* States */

  const [isFullscreen, setIsFullscreen] = useState(false);

  /* API Calls */

  /* Functions & Functional Components */
  const handleResetBounds = () => {
    window.dispatchEvent(new CustomEvent(resetBoundsEventName));
  };

  const handleOpenFullscreen = () => {
    setIsFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  return (
    <>
      <WidgetCard
        title={title}
        subheader={subheader}
        avatar={
          avatar ?? (
            <Box
              sx={{
                padding: `${theme.tokens.spacing.sm}px`,
                height: `${theme.tokens.spacing.sm * 5}px`,
                width: `${theme.tokens.spacing.sm * 5}px`,
                mx: `-${theme.tokens.spacing.sm}px`,
                borderRadius: theme.tokens.borderRadius.full,
                background: theme.tokens.colors.card,
                boxShadow: theme.tokens.shadows.sm,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.main,
              }}
            >
              <FmdGoodOutlined />
            </Box>
          )
        }
        cardContentProps={{ sx: { padding: '0px !important' } }}
        onHeaderClick={
          requireApplicationSelection && isAllSelection(appId)
            ? undefined
            : (onHeaderClick ??
              (() => navigate(getApplicationScopedPath('map', pages.map.path))))
        }
        action={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title='Reset map view'>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleResetBounds();
                }}
                size='small'
                sx={{ mb: 'auto' }}
              >
                <RestartAlt />
              </IconButton>
            </Tooltip>
            <Tooltip title='Fullscreen'>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenFullscreen();
                }}
                size='small'
                sx={{ mb: 'auto' }}
              >
                <Fullscreen />
              </IconButton>
            </Tooltip>
          </Box>
        }
      >
        {mapContent ?? <DevicesMap />}
      </WidgetCard>

      {(renderFullscreen ?? ((props) => <MapFullscreenModal {...props} />))({
        open: isFullscreen,
        onClose: handleCloseFullscreen,
      })}
    </>
  );
};

export default MapWidget;
